import React, { useState, useRef, useCallback } from 'react';
import {
  StepUploadIcon,
  StepAnalyzeIcon,
  StepResultsIcon,
} from './Illustrations';

const MAX_SIZE_MB = 250;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export default function FileUpload({ onUpload }) {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState('');
  // Selected file held here until the user acknowledges the disclaimer —
  // analysis must not start before the not-legal-advice notice is accepted.
  const [pendingFile, setPendingFile] = useState(null);
  const inputRef = useRef(null);

  function validate(file) {
    if (!file) return 'No file selected';
    if (file.type !== 'application/pdf') return 'Only PDF files are accepted. Please upload a .pdf file.';
    if (file.size > MAX_SIZE_BYTES) return `File is too large. Maximum size is ${MAX_SIZE_MB}MB.`;
    return null;
  }

  const handleFile = useCallback((file) => {
    const err = validate(file);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError('');
    setPendingFile(file);
  }, []);

  function confirmDisclaimer() {
    const file = pendingFile;
    setPendingFile(null);
    if (file) onUpload(file);
  }

  function cancelDisclaimer() {
    setPendingFile(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  function handleChange(e) {
    if (e.target.files?.[0]) {
      handleFile(e.target.files[0]);
    }
  }

  return (
    <div className="upload-container">
      <div className="hero-section">
        <div className="upload-intro">
          <span className="hero-badge">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
            </svg>
            AI-powered Section 32 analysis
          </span>
          <h1>
            Know what you're signing,
            <br />
            <span className="grad-text">before you sign it.</span>
          </h1>
          <p>
            Upload your Victorian Section 32/Contract of Sale and get a clear,
            easy-to-read breakdown of risks, fees, and the questions to ask
            before you buy.
          </p>
        </div>
      </div>

      <div
        className={`dropzone ${dragActive ? 'dropzone-active' : ''}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleChange}
          hidden
        />
        <div className="dropzone-orb">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </div>
        <p className="dropzone-text">Drop your Section 32 here</p>
        <p className="dropzone-subtext">
          Drag &amp; drop your Section 32/Contract of Sale, or
        </p>
        <span className="dropzone-cta">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Choose a PDF
        </span>
        <div className="dropzone-meta">
          <span>PDF only</span>
          <span>Up to {MAX_SIZE_MB}MB</span>
          <span>Deleted after analysis</span>
        </div>
      </div>

      {pendingFile && (
        <div className="paywall-overlay" role="dialog" aria-modal="true" aria-label="Important notice before analysis">
          <div className="paywall-modal disclaimer-modal">
            <h3>Before we analyse your document</h3>
            <p className="disclaimer-lead">
              <strong>Prop Doc does not provide legal advice.</strong>
            </p>
            <p>
              This is an automated tool that helps you read and understand the contents of a
              Victorian Section 32/Contract of Sale. The report it produces is general
              information only — it is not legal advice, and it may not identify every issue
              in your document.
            </p>
            <p>
              Before signing a contract of sale, always have the Section 32 and contract
              reviewed by a licensed conveyancer or an Australian legal practitioner.
            </p>
            <div className="disclaimer-actions">
              <button className="btn btn-primary" onClick={confirmDisclaimer}>
                I understand — analyse my document
              </button>
              <button className="btn btn-secondary" onClick={cancelDisclaimer}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {validationError && (
        <div className="validation-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {validationError}
        </div>
      )}

      <div className="how-it-works">
        <h3>How It Works</h3>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <StepUploadIcon />
            <h4>Upload PDF</h4>
            <p>Drop your Section 32/Contract of Sale right here. Your file stays private and is deleted after analysis.</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <StepAnalyzeIcon />
            <h4>AI Analysis</h4>
            <p>Our AI reads every page, identifying risks, fees, and important details a buyer needs to know.</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <StepResultsIcon />
            <h4>Get Results</h4>
            <p>Receive a clear report with colour-coded risks, fee breakdowns, and questions to ask your agent.</p>
          </div>
        </div>
      </div>

      <div className="upload-info">
        <div className="info-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4A3220" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <div>
          <h3>What is a Section 32?</h3>
          <p>
            A Section 32 (vendor's statement) is a legal document required under
            the Victorian <em>Sale of Land Act 1962</em>. It discloses important
            information about a property including title details, planning
            restrictions, outgoings, and any known issues. You'll receive this
            document from the real estate agent or vendor's solicitor before
            signing a contract of sale.
          </p>
        </div>
      </div>
    </div>
  );
}
